import { account } from '../core/account.js';
import { catalog } from '../core/catalog.js';
import { customOrder } from '../core/custom-order.js';
import { applyGroupOrder, moveTo, moveChannelIds, orderIds } from '../core/order-core.js';
import { device } from '../core/device.js';
import { epg } from '../core/epg.js';
import { settings } from '../core/storage.js';
import { filters } from '../core/filters.js';
import { t } from '../core/i18n.js';
import { debounce, escapeHtml, cleanName } from '../core/utils.js';
import { notify } from '../core/notify.js';
import { bindVoice } from '../core/voice.js';
import { createStage } from '../ui/live-stage.js';
import { createPanel } from '../ui/live-panel.js';
import { createLiveFilters } from '../ui/live-filters.js';
import { channelCard, channelRow, filterBar, pageHead, skeletonGrid, empty, enhance, infiniteScroll, bindViewToggle, groupByCategory, categorySections } from '../ui/render.js';

/** La vista elegida. «En pantalla» está oculta por ahora (estorbaba al llegar a «Panel» con el mando): quien la tuviera guardada vuelve a las filas. */
/** El «Panel» (categorías · canales · ventanita) necesita pantalla ancha: en el móvil no se ofrece (la tele y el escritorio, sí). */
const panelAllowed = () => device.isTV || document.documentElement.classList.contains('mode-tv') || (typeof window !== 'undefined' && window.innerWidth >= 820);
const liveViewSetting = () => { const v = settings.get('liveView') || 'rows'; return v === 'stage' || (v === 'panel' && !panelAllowed()) ? 'rows' : v; };
const ROWS_CHUNK = 5; // filas (categorías) por tanda en la vista «Por categorías»
const CHUNK = 60;
export default {
    path: '/live/',
    nav: 'live',

    render(route) {
        return `<div class="page" data-name="live">
            <div class="page-content">
                <div class="page-sticky">
                ${pageHead({
                    title: t('live.title'),
                    bar: true,
                    search: true,
                    searchPlaceholder: t('live.search'),
                    actions: `<a class="btn btn-icon" href="#" data-href="/guide/" aria-label="${t('guide.title')}"><span class="mi">calendar_view_day</span></a>`
                })}
                <div data-filters></div>
                <div class="live-toolbar">
                    <div class="page-sub" data-count></div>
                    <div class="live-tools" data-nav-row>
                        <button class="btn btn-sm live-sort" data-sort aria-label="${t('live.sortBy')}"><span class="mi">swap_vert</span><span data-sort-label>${t('live.sortBy')}</span><span class="mi">expand_more</span></button>
                        <div class="seg" role="group" aria-label="${t('live.viewGrid')} / ${t('live.viewList')}">
                            <button class="seg-btn ${liveViewSetting() === 'grid' ? 'is-active' : ''}" data-view="grid" aria-label="${t('live.viewGrid')}"><span class="mi">grid_view</span></button>
                            <button class="seg-btn ${liveViewSetting() === 'rows' ? 'is-active' : ''}" data-view="rows" aria-label="${t('live.viewRows')}"><span class="mi">view_stream</span></button>
                            <button class="seg-btn ${liveViewSetting() === 'list' ? 'is-active' : ''}" data-view="list" aria-label="${t('live.viewList')}"><span class="mi">view_list</span></button>
                            ${panelAllowed() ? `<button class="seg-btn ${liveViewSetting() === 'panel' ? 'is-active' : ''}" data-view="panel" aria-label="${t('live.viewPanel')}" title="${t('live.viewPanel')}"><span class="mi">view_sidebar</span></button>` : ''}
                        </div>
                    </div>
                </div>
                </div>
                <div data-list>${catalog.loaded ? '' : skeletonGrid('channel')}</div>
                <div data-sentinel style="height:1px"></div>
            </div>
        </div>`;
    },

    mount(page, route) {
        const root = page.el;
        // La selección (ámbito, proveedores, categorías, país, calidad y orden) es la misma que usa la Guía de TV; sin categoría en la
        // dirección se vuelve a lo último que eligió este espectador.
        const lf = createLiveFilters({ routeCat: (route.query && route.query.cat) || '' });
        const state = lf.state;
        Object.assign(state, { q: '', view: liveViewSetting(), shown: 0, items: [] });
        const country = lf.country;
        const els = {
            filters: root.querySelector('[data-filters]'),
            count: root.querySelector('[data-count]'),
            list: root.querySelector('[data-list]'),
            sentinel: root.querySelector('[data-sentinel]'),
            search: root.querySelector('[data-search]'),
            clear: root.querySelector('[data-search-clear]'),
            sortBtn: root.querySelector('[data-sort]'),
            sortLabel: root.querySelector('[data-sort-label]'),
            seg: root.querySelector('.seg')
        };
        const compute = () => {
            const items = lf.search(lf.items(), state.q);
            state.items = items;
            catalog.zapList = items; // el reproductor zapea y lista justo estos canales (los filtros elegidos aquí)
        };

        // Las hileras por categorías: por último visto (lo de siempre) o, en «Mi orden», como las ordenó la persona.
        const liveGroups = () => (state.sort === 'mine'
            ? applyGroupOrder(groupByCategory(state.items, catalog.categories.live, null), customOrder.get('live'))
            : groupByCategory(state.items, catalog.categories.live, catalog.watchedAt('live')));

        const renderCats = () => {
            const { chips, sort, sortActive } = lf.bar();
            els.filters.innerHTML = filterBar(chips);
            els.sortLabel.textContent = state.sort === 'default' ? t('live.sortBy') : sort.name;
            els.sortBtn.classList.toggle('is-active', sortActive);
        };

        let stage = null; // vista «En pantalla»: canal en grande y la hilera de canales debajo
        const leaveStage = () => { if (stage) { stage.destroy(); stage = null; } };
        let panel = null; // vista «Panel»: categorías, canales y ventanita con su programación
        const leavePanel = () => { if (panel) { panel.destroy(); panel = null; } };
        // «Cambiar vista» / atrás: se vuelve a la vista que había antes de «En pantalla» (o a las filas por categorías).
        const exitStage = () => {
            const back = 'rows'; // «Cambiar vista» / atrás: la vista por defecto (filas por categorías)
            state.view = back; // solo esta visita: la vista por defecto (Ajustes → Vista de TV en directo) no se toca
            els.seg.querySelectorAll('[data-view]').forEach((b) => b.classList.toggle('is-active', b.getAttribute('data-view') === back));
            renderList(true);
        };
        // Ordenar en la vista «Panel» (pulsación larga): categorías y canales de una categoría; se guarda como «Mi orden».
        const WHERE = { first: 'order.moveFirst', up: 'order.up', down: 'order.down', last: 'order.moveLast' };
        const ICON = { first: 'first_page', up: 'expand_less', down: 'expand_more', last: 'last_page' };
        const saved = () => { settings.set('liveSort', 'mine'); document.dispatchEvent(new CustomEvent('iptv:live-order')); };
        const moveCategory = (id, ids, where) => {
            const i = ids.indexOf(String(id));
            const to = { first: 0, up: i - 1, down: i + 1, last: ids.length - 1 }[where];
            if (i < 0 || to < 0 || to >= ids.length || to === i) return notify.toast(t('order.cantMove'), { icon: 'info', duration: 2500 });
            customOrder.set('live', { cats: moveTo(ids, i, to) });
            if (panel) panel.focusAfter({ cat: String(id) });
            saved();
        };
        const moveChannel = (catId, ids, id, where) => {
            if (catId === '__all__') return;
            const next = moveChannelIds(ids, ids, id, { first: 'first', up: 'left', down: 'right', last: 'last' }[where]);
            if (next.join('|') === ids.join('|')) return notify.toast(t('order.cantMove'), { icon: 'info', duration: 2500 });
            customOrder.set('live', { chans: { [catId]: next } });
            if (panel) panel.focusAfter({ ch: String(id) });
            saved();
        };
        const openPanelCategoryMenu = (cat, ids) => {
            const entry = (icon, label, onClick) => ({ text: `<span class="mi">${icon}</span>${label}`, onClick });
            window.app.actions.create({ cssClass: 'main-menu', buttons: [[
                { text: escapeHtml(cat.name), label: true },
                ...['first', 'up', 'down', 'last'].map((w) => entry(ICON[w], t(WHERE[w]), () => moveCategory(cat.id, ids, w))),
                customOrder.has('live') ? entry('restart_alt', t('order.reset'), () => { customOrder.reset('live'); settings.set('liveSort', 'default'); document.dispatchEvent(new CustomEvent('iptv:live-order')); }) : null
            ].filter(Boolean), [{ text: t('common.cancel'), color: 'red' }]] }).open();
        };
        const openPanelChannelMenu = (ch, catId, ids) => {
            const entry = (icon, label, onClick) => ({ text: `<span class="mi">${icon}</span>${label}`, onClick });
            const fav = catalog.isFavorite('live', ch.id);
            window.app.actions.create({ cssClass: 'main-menu', buttons: [[
                { text: escapeHtml(cleanName(ch.name)), label: true },
                entry('play_arrow', t('common.play'), () => window.IPTV.actions.playLive(ch.id)),
                entry(fav ? 'bookmark_remove' : 'bookmark_add', fav ? t('common.favoriteRemove') : t('common.favoriteAdd'), () => window.IPTV.actions.toggleFavorite('live', ch.id)),
                ...(catId !== '__all__' ? ['first', 'up', 'down', 'last'].map((w) => entry(ICON[w], t(WHERE[w]), () => moveChannel(catId, ids, ch.id, w))) : [])
            ], [{ text: t('common.cancel'), color: 'red' }]] }).open();
        };
        const renderList = (reset) => {
            compute();
            if (state.view === 'panel' && state.items.length) {
                els.list.className = '';
                els.count.textContent = state.items.length.toLocaleString() + ' ' + t('common.channels');
                state.shown = state.items.length;
                leaveStage();
                if (!panel) {
                    const sc = root.querySelector('.page-content');
                    if (sc) sc.scrollTo({ top: 0 });
                    els.list.innerHTML = '';
                    panel = createPanel(els.list, { onOpen: (id) => window.IPTV.actions.playLive(id), onExit: () => exitStage(), onChannelMenu: (ch, catId, ids) => openPanelChannelMenu(ch, catId, ids), onCategoryMenu: (cat, ids) => openPanelCategoryMenu(cat, ids) });
                }
                panel.setData(state.sort === 'mine' ? liveGroups() : groupByCategory(state.items, catalog.categories.live, null));
                return;
            }
            leavePanel();
            if (state.view === 'stage' && state.items.length) {
                els.list.className = '';
                els.count.textContent = state.items.length.toLocaleString() + ' ' + t('common.channels');
                state.shown = state.items.length; // no hay «más» que pintar al desplazar
                if (!stage) {
                    const sc = root.querySelector('.page-content');
                    if (sc) sc.scrollTo({ top: 0 });
                    els.list.innerHTML = '';
                    stage = createStage(els.list, { onOpen: (id) => window.IPTV.actions.playLive(id), onExit: () => exitStage() });
                }
                // Si se viene de las filas por categorías, la hilera también va agrupada por categorías.
                const grouped = (state.prevView || 'rows') === 'rows' && !state.q;
                stage.setItems(state.items, grouped ? liveGroups() : null);
                return;
            }
            leaveStage();
            leavePanel();
            if (reset) {
                state.shown = 0;
                els.list.innerHTML = '';
                els.list.className = state.view === 'list' ? 'chlist' : state.view === 'rows' ? 'cat-rows' : 'grid grid-channels';
                state.groups = null;
            }
            els.count.textContent = state.items.length.toLocaleString() + ' ' + t('common.channels');
            if (!state.items.length) {
                els.list.className = '';
                // Ninguna fuente trae canales (no es una búsqueda ni un filtro): se ofrece añadir un proveedor.
                const none = !state.q && catalog.visible('live').length === 0;
                els.list.innerHTML = none
                    ? empty({ icon: 'live_tv', title: t('live.none'), text: t(account.demo ? 'demo.noneHint' : 'lib.noneHint'), action: account.demo ? '' : `<div class="flex"><button type="button" class="btn btn-accent" data-href="/settings/?add=source" data-nav-default><span class="mi">add</span>${escapeHtml(t('settings.addProfile'))}</button></div>` })
                    : empty({ title: t('common.noResults'), text: t('common.noResultsHint') });
                return;
            }
            if (state.view === 'rows') {
                // Una fila horizontal por categoría (como en las plataformas): se pintan de pocas en pocas al desplazar.
                if (!state.groups) state.groups = liveGroups();
                els.list.insertAdjacentHTML('beforeend', categorySections(state.groups, { from: state.shown, count: ROWS_CHUNK, cardFor: channelCard, kind: 'channel' }));
                state.shown += ROWS_CHUNK;
                enhance(els.list);
                return;
            }
            const next = state.items.slice(state.shown, state.shown + CHUNK);
            state.shown += next.length;
            const html = next.map((ch) => (state.view === 'list' ? channelRow(ch) : channelCard(ch))).join('');
            els.list.insertAdjacentHTML('beforeend', html);
            enhance(els.list);
        };

        // Se movió un canal con el menú de pulsación larga: se repinta en «Mi orden» sin perder dónde estaba la persona.
        const onOrderChanged = () => {
            if (!root.isConnected) return;
            const sc = root.querySelector('.page-content');
            const top = sc ? sc.scrollTop : 0;
            const was = state.shown;
            state.sort = 'mine';
            renderCats();
            renderList(true);
            for (let i = 0; i < 200 && state.shown < was && state.shown < (state.view === 'rows' ? (state.groups || []).length : state.items.length); i++) renderList(false);
            if (sc) sc.scrollTop = top;
        };
        document.addEventListener('iptv:live-order', onOrderChanged);

        const refreshAll = () => {
            if (!catalog.loaded) return;
            renderCats();
            renderList(true);
        };

        els.filters.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-filter]');
            if (!btn) return;
            lf.open(btn.getAttribute('data-filter'), (what) => {
                if (what && what.unlocked) { refreshAll(); return; }
                renderCats();
                renderList(true);
            });
        });
        bindVoice(root);
        bindViewToggle(root);
        els.search.addEventListener('input', debounce(() => {
            state.q = els.search.value.trim();
            els.clear.hidden = !state.q;
            renderList(true);
        }, 250));
        els.clear.addEventListener('click', () => {
            els.search.value = '';
            state.q = '';
            els.clear.hidden = true;
            renderList(true);
            els.search.focus();
        });
        els.seg.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-view]');
            if (!btn) return;
            const view = btn.getAttribute('data-view');
            if (view === state.view) return;
            if (view === 'stage' || view === 'panel') state.prevView = state.view === 'stage' || state.view === 'panel' ? state.prevView : state.view;
            state.view = view;
            settings.set('liveView', view);
            els.seg.querySelectorAll('[data-view]').forEach((b) => b.classList.toggle('is-active', b === btn));
            renderList(true);
        });
        const openSort = () => lf.open('sort', () => { renderCats(); renderList(true); });
        els.sortBtn.addEventListener('click', openSort);

        const more = () => (state.view === 'stage' || state.view === 'panel' ? false : state.view === 'rows' ? !!state.groups && state.shown < state.groups.length : state.shown < state.items.length);
        const stopInfinite = infiniteScroll(els.sentinel, () => {
            if (catalog.loaded && more()) renderList(false);
        });
        // «Ver todo» de una fila: esa categoría sola, en cuadrícula.
        els.list.addEventListener('click', (e) => {
            const all = e.target.closest('[data-cat-all]');
            if (!all) return;
            e.preventDefault();
            state.wanted = [all.getAttribute('data-cat-all')];
            filters.set('live', { cats: state.wanted });
            // «Ver todo» enseña esa categoría en cuadrícula solo esta vez: no cambia la vista elegida por defecto.
            state.view = 'grid';
            els.seg.querySelectorAll('[data-view]').forEach((x) => x.classList.toggle('is-active', x.getAttribute('data-view') === 'grid'));
            refreshAll();
            const sc = root.querySelector('.page-content');
            if (sc) sc.scrollTo({ top: 0 });
        });

        // Al cerrar el reproductor se vuelve a la lista con el canal que se estaba viendo (el último al que se cambió) a la vista y
        // marcado; con el mando, con el foco puesto en él. Si está más abajo de lo pintado, se pintan tandas hasta llegar a él.
        const revealPlaying = () => {
            const player = window.IPTV && window.IPTV.player;
            const ch = player && player.mode === 'live' ? player.item : null;
            if (!ch || !root.isConnected || !root.classList.contains('page-current') || !catalog.loaded) return;
            const find = () => els.list.querySelector(`[data-play-live="${window.CSS && CSS.escape ? CSS.escape(String(ch.id)) : String(ch.id)}"]`);
            let el = find();
            for (let guard = 0; !el && more() && guard < 300; guard++) { renderList(false); el = find(); }
            if (!el) return;
            els.list.querySelectorAll('.chitem.is-current').forEach((x) => x.classList.remove('is-current'));
            if (el.classList.contains('chitem')) el.classList.add('is-current');
            el.scrollIntoView({ block: 'center', inline: 'center' });
            if (device.isTV && window.IPTV.tvnav) window.IPTV.tvnav.focus(el, { scroll: false });
        };
        let lastFav = 0;
        const offClose = window.IPTV && window.IPTV.player ? window.IPTV.player.on('close', () => setTimeout(revealPlaying, 80)) : null;

        refreshAll();
        const off = [
            // Marcar o quitar de Mi lista no rehace la lista (se perdería el sitio donde estás y, en la tele, el foco): solo se
            // ponen al día los selectores. Con el filtro «favoritos» puesto sí hay que rehacerla.
            catalog.on('favorites', () => { lastFav = Date.now(); }),
            catalog.on('change', debounce(() => { if (Date.now() - lastFav < 1500 && state.scope !== 'fav') renderCats(); else refreshAll(); }, 200)),
            epg.on('change', debounce(() => { if (state.view === 'stage' && stage) stage.refreshInfo(); else if (state.view === 'panel' && panel) panel.refreshInfo(); else renderList(true); }, 400))
        ];
        if (offClose) off.push(offClose);
        // Al irse de la página (otra pestaña) el vídeo de la vista «En pantalla» se para; al volver, sigue.
        const onOut = (e) => { if (e.target === root) { if (stage) stage.pause(); if (panel) panel.pause(); } };
        const onIn = (e) => { if (e.target === root) { if (stage) stage.resume(); if (panel) panel.resume(); } };
        root.addEventListener('page:beforeout', onOut);
        root.addEventListener('page:beforein', onIn);
        return () => {
            root.removeEventListener('page:beforeout', onOut);
            root.removeEventListener('page:beforein', onIn);
            document.removeEventListener('iptv:live-order', onOrderChanged);
            leaveStage();
            leavePanel();
            stopInfinite();
            off.forEach((fn) => fn());
            if (catalog.zapList === state.items) catalog.zapList = null;
        };
    }
};
